// src/tattoo/LayersPanel.jsx
import React, { useState } from "react";
import { useSnapshot } from "valtio";
import {
  tattoo,
  removeLayer,
  moveLayer,
  saveProjectToFile,
  loadProjectFromFile,
  exportUVSheetPNG,
  exportDesignsPNG,
  editLayer,
  setLayerVisible,
  replaceLayerImage,
  exportProjectHashAsync,
  getActivePartKey,
  mergeLayerDown,
} from "./state";
import { useSubscription } from "./hooks/useSubscription";
import UpgradeModal from "./billing/UpgradeModal.jsx";
import DecalEditor from "./DecalEditor.jsx";
import {
  commentsStore,
  unresolvedCount,
  unackedCount,
} from "../comments/commentsState";
import CommentsThread from "../comments/CommentsThread.jsx";

import {
  reactionsStore,
  getVote,
  toggleVote,
  preloadReactionCounts,
} from "./reactionsState";
import CopyLinkButton from "./CopyLinkButton.jsx";
import { fetchComments } from "../comments/commentsApi.js";
import { makeShareLinkResilient } from "./share";
import { Link } from "react-router-dom";

import {
  Eye,
  EyeOff,
  ChevronUp,
  Pencil,
  Layers,
  ArrowUp,
  ArrowDown,
  Copy,
  Trash2,
  MessageCircle,
  Heart,
  Type,
} from "lucide-react";
import { openTextDialog } from "./components/TextLayerDialog.jsx";

/* ---------------- helpers ---------------- */

// Creates a new layer from a dataURL and opens it
function addFullUVLayerFromDataURL({
  dataURL,
  name = "Paint",
  source = "upload",
} = {}) {
  if (!dataURL) return;

  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `layer_${Date.now().toString(36)}_${Math.random()
          .toString(36)
          .slice(2, 6)}`;

  // Use full-UV transform (covers the whole atlas)
  const fullUVTransform = {
    transform: { tx: 0, ty: 0, sx: 1, sy: 1, rot: 0 },
    mapping: "uv",
  };

  // Respect current context so paint-layers are NOT “global”
  const sex = tattoo.sex || "female";
  const part = getActivePartKey() || "fullbody";

  const next = {
    id,
    name,
    url: dataURL,
    source,
    visible: true,
    opacity: 1,
    blend: "normal",
    createdAt: Date.now(),
    sex,
    part,
    partKeys: [part],
    maskUrl: null,
    ...fullUVTransform,
  };

  tattoo.layers = [next, ...(tattoo.layers || [])];
  tattoo.editingLayerId = id;

  try {
    window.dispatchEvent(
      new CustomEvent("tt:open-layer", { detail: { layerId: id } })
    );
  } catch {}
}

function isLikelyPlaceholder(layer) {
  const name = (layer?.name || "").trim().toLowerCase();
  const url = typeof layer?.url === "string" ? layer.url.trim() : "";
  if ((name === "guide paint" || name === "guidepaint") && !url) return true;
  if (url === "null" || url === "undefined" || url === "about:blank")
    return true;
  return false;
}

/* ---------------- selector ---------------- */
export function pickLayerForMeshEdit(layerId) {
  tattoo.editingLayerId = layerId;

  // single source for mesh drag system
  const d = (window.__tt_decal_drag = window.__tt_decal_drag || {});
  d.layerId = layerId;
  d.active = false;
  d.tx = null;
  d.ty = null;
  d.hitWorld = null;

  // 🔥 important: clear “first one served” latch
  window.__tt_decal_drag_active = false;
  window.__tt_decal_drag_pointerId = null;
}
/* ---------------- Row ---------------- */

function Row({ layer, expanded, partKey, onToggle }) {
  const snap = useSnapshot(tattoo);
  const commentsSnap = useSnapshot(commentsStore);
  const isReadOnly = !!tattoo.readOnly;
  const rxSnap = useSnapshot(reactionsStore);
  void rxSnap.byLayer[layer.id];

  const activeId = snap?.editingLayerId || tattoo.editingLayerId || null;
  const isActive = layer.id === activeId;

  const activeBlend = (layer.blend || "ink").toLowerCase();

  void commentsSnap.threads[layer.id];
  const unresolved = unackedCount(layer.id);

  const hasImage = typeof layer.url === "string" && layer.url.trim() !== "";

  const addRef = React.useRef(null);
  const cardRef = React.useRef(null);

  const onReplaceImage = (e) => {
    if (isReadOnly) return;
    const f = e.target.files?.[0];
    if (!f) return;
    replaceLayerImage(layer.id, f, { name: f.name.replace(/\.[^.]+$/, "") });
    e.target.value = "";
  };

  const onSetMask = (e) => {
    if (isReadOnly) return;
    const f = e.target.files?.[0];
    if (!f) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataURL = reader.result;
      tattoo.layers = (tattoo.layers || []).map((L) =>
        L.id === layer.id ? { ...L, maskUrl: dataURL } : L
      );
    };
    reader.readAsDataURL(f);

    e.target.value = "";
  };

  const idx = (tattoo.layers || []).findIndex((l) => l.id === layer.id);
  const canUp = idx > 0;
  const canDown = idx >= 0 && idx < (tattoo.layers?.length || 1) - 1;

  const vote = getVote(layer.id);

  function makeLayerId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `layer_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 6)}`;
  }

  function duplicateLayer(e) {
    e.stopPropagation();
    if (isReadOnly) return;

    const all = tattoo.layers || [];
    const idx = all.findIndex((l) => l.id === layer.id);
    if (idx < 0) return;

    const clone = {
      ...layer,
      id: makeLayerId(),
      name: (layer.name || "Layer") + " copy",
      createdAt: Date.now(),
    };

    const next = [...all];
    // insert just above original to keep z-order intuitive
    next.splice(idx, 0, clone);
    tattoo.layers = next;
    tattoo.editingLayerId = clone.id;

    try {
      window.dispatchEvent(
        new CustomEvent("tt:open-layer", { detail: { layerId: clone.id } })
      );
    } catch {}
  }

  // ----- NEW: blend + opacity helpers -----
  const modeRaw = (layer.blend || "skin").toLowerCase();
  const mode =
    modeRaw === "overlay" ? "white" : modeRaw === "ink" ? "skin" : modeRaw;
  const opacity = typeof layer.opacity === "number" ? layer.opacity : 1;

  const setLayerProps = (patch) => {
    if (isReadOnly) return;
    tattoo.layers = (tattoo.layers || []).map((l) =>
      l.id === layer.id ? { ...l, ...patch } : l
    );
  };

  const setBlendMode = (blend) => {
    if (isReadOnly) return;
    // we use "white" here, pipeline will map it to overlay
    setLayerProps({ blend });
  };

  const handleOpacityChange = (e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    const v = Number(e.target.value);
    const clamped = Math.max(0.05, Math.min(1, v));
    setLayerProps({ opacity: clamped });
  };
  const I = ({ icon: Icon, ...props }) => (
    <Icon size={16} strokeWidth={2.25} aria-hidden="true" {...props} />
  );

  const iconBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    width: 30,
  };

  return (
    <div
      ref={cardRef}
      className={`layer-card ${expanded ? "is-open" : ""} ${
        isActive ? "is-active" : ""
      }`}
      data-layer-id={layer.id}
      data-active={isActive ? "true" : "false"}
      onClick={() => onToggle(layer.id)}
    >
      {/* Name row (always visible) */}
      <div className="layer-top" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <input
            className="layer-name"
            value={layer.name || ""}
            onChange={(e) => {
              if (isReadOnly) return;
              tattoo.layers = tattoo.layers.map((l) =>
                l.id === layer.id
                  ? { ...l, name: e.target.value.slice(0, 64) }
                  : l
              );
            }}
            placeholder="Layer name"
            aria-label="Layer name"
            readOnly={isReadOnly}
          />

          {layer.source === "flash" &&
            layer.ownerShopName &&
            (layer.ownerShopSlug ? (
              <Link
                to={`/artists/${layer.ownerShopSlug}`}
                onClick={(e) => e.stopPropagation()}
                className="tt-layer-source-badge"
                style={{
                  marginTop: 3,
                  fontSize: 10,
                  padding: "3px 8px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  opacity: 0.95,
                  width: "fit-content",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  transition:
                    "background 0.16s ease, border-color 0.16s ease, transform 0.12s ease",
                }}
                title={`View ${layer.ownerShopName}`}
              >
                <span>Flash</span>
                <span>·</span>
                <span>{layer.ownerShopName}</span>
              </Link>
            ) : (
              <span
                style={{
                  marginTop: 3,
                  fontSize: 10,
                  padding: "2px 6px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  opacity: 0.85,
                  width: "fit-content",
                }}
                title={`Flash owned by ${layer.ownerShopName}`}
              >
                Flash · {layer.ownerShopName}
              </span>
            ))}
        </div>

        {/* Comments (always visible) */}
        <button
          type="button"
          className="btn-icon layer-top-btn"
          title="Comments"
          onClick={(e) => {
            e.stopPropagation();
            const willOpen = !expanded;
            if (willOpen) onToggle(layer.id);
            setTimeout(
              () => {
                window.dispatchEvent(
                  new CustomEvent("tt:focus-comments", {
                    detail: { layerId: layer.id },
                  })
                );
              },
              willOpen ? 100 : 0
            );
          }}
          style={{ position: "relative" }}
        >
          <I icon={MessageCircle} />
          {unresolved > 0 && (
            <span
              className="layer-badge"
              aria-label={`${unresolved} unresolved`}
              title={`${unresolved} unresolved`}
            >
              {unresolved}
            </span>
          )}
        </button>

        {/* Heart (always visible) */}
        <button
          type="button"
          className="btn-icon layer-top-btn"
          title={vote.mine ? "Remove heart" : "Heart this design"}
          onClick={(e) => {
            e.stopPropagation();
            toggleVote(layer.id, "local");
          }}
        >
          <span className="layer-heart">
            <Heart
              size={16}
              strokeWidth={2.25}
              aria-hidden="true"
              fill={vote.mine ? "currentColor" : "none"}
            />
            <span className="layer-heart-count">{vote.count || 0}</span>
          </span>
        </button>
      </div>

      {/* Header */}
      <div className="layer-head" onClick={(e) => e.stopPropagation()}>
        {/* Eye / visibility */}
        <button
          type="button"
          className={`btn-icon eye ${layer.visible ? "on" : ""}`}
          title={layer.visible ? "Hide" : "Show"}
          onClick={(e) => {
            e.stopPropagation();

            // Don't call setLayerVisible if it might be blocked in readOnly;
            // just flip visibility locally (viewer needs this).
            tattoo.layers = (tattoo.layers || []).map((L) =>
              L.id === layer.id ? { ...L, visible: !L.visible } : L
            );
          }}
          aria-pressed={layer.visible ? "true" : "false"}
        >
          {layer.visible ? <I icon={Eye} /> : <I icon={EyeOff} />}
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 4,
            fontSize: 11,
          }}
        ></div>

        <div className="order">
          <button
            type="button"
            className={`btn-icon merge ${expanded ? "btn-ghost" : ""}`}
            title={expanded ? "Collapse" : "Edit UV / placement"}
            onClick={(e) => {
              e.stopPropagation();
              pickLayerForMeshEdit(layer.id);
              onToggle(layer.id);
            }}
          >
            {expanded ? <I icon={ChevronUp} /> : <I icon={Pencil} />}
          </button>
          <button
            className="btn"
            title="Merge down"
            onClick={(e) => {
              e.stopPropagation();

              tattoo.ui.mergeConfirm = {
                open: true,
                layerId: layer.id,
                partKey: tattoo.viewMode || tattoo.activePart || "torso",
              };
            }}
            disabled={
              isReadOnly ||
              !canDown ||
              layer.locked ||
              tattoo.layers?.[idx + 1]?.locked
            }
          >
            <span style={iconBtnStyle}>
              <I icon={Layers} />
              <I icon={ArrowDown} />
            </span>
          </button>

          {/* Move up/down */}
          <button
            className="btn"
            title="Move up"
            onClick={() => !isReadOnly && moveLayer(layer.id, "up")}
            disabled={isReadOnly || !canUp}
          >
            <I icon={ArrowUp} />
          </button>
          <button
            className="btn"
            title="Move down"
            onClick={() => !isReadOnly && moveLayer(layer.id, "down")}
            disabled={isReadOnly || !canDown}
          >
            <I icon={ArrowDown} />
          </button>

          {/* Expand (allowed in view mode so comments can be read) */}

          {!isReadOnly && (
            <>
              {layer.kind === "text" && (
                <button
                  className="btn"
                  title="Edit text"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTextDialog(layer.id);
                  }}
                >
                  <I icon={Type} />
                </button>
              )}
              <button
                className="btn"
                title="Duplicate layer"
                onClick={duplicateLayer}
              >
                <I icon={Copy} />
              </button>

              <button
                className="btn"
                title="Delete layer"
                onClick={(e) => {
                  e.stopPropagation();
                  const ok = window.confirm(
                    "Delete this layer? This cannot be undone..."
                  );
                  if (!ok) return;
                  removeLayer(layer.id);
                }}
              >
                <I icon={Trash2} />
              </button>
            </>
          )}
        </div>
      </div>
      {/* Comments – both app + /view use the same thread */}

      {/* Attached editor */}
      {expanded && (
        <div className="layer-body" onClick={(e) => e.stopPropagation()}>
          {/* Actions row */}
          <div
            className="row"
            style={{
              gap: 8,
              margin: "0 0 8px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <CommentsThread layerId={layer.id} />
          </div>
          <div
            className="row"
            style={{
              gap: 8,
              margin: "0 0 8px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Share link: only for editors (hide in viewer) */}
            {!isReadOnly && (
              <div className="tt-row-2">
                <CopyLinkButton
                  makeUrl={async () => {
                    try {
                      const camObj =
                        typeof window.tt_getCam === "function"
                          ? window.tt_getCam()
                          : null;

                      // ✅ IMPORTANT: encode cam as a string so URLSearchParams won’t destroy it
                      const cam =
                        camObj && camObj.pos && camObj.target
                          ? encodeURIComponent(btoa(JSON.stringify(camObj)))
                          : null;

                      const payload = await exportProjectHashAsync();

                      const url = await makeShareLinkResilient({
                        payload,
                        extras: {
                          r: 1,
                          focus: String(layer.id),
                          ...(cam ? { cam } : {}),
                        },
                      });

                      return url;
                    } catch (err) {
                      console.warn("[share] build link error:", err);
                      return window.location.origin + "/view";
                    }
                  }}
                  label="Share for Feedback"
                />

                <label className="btn" style={{ cursor: "pointer" }}>
                  <input
                    ref={addRef}
                    type="file"
                    accept="image/*"
                    onChange={onReplaceImage}
                    style={{ display: "none" }}
                  />
                  Replace Image
                </label>
              </div>
            )}

            <div className="tt-layer-controls">
              <div className="blend-toggle">
                {["block", "ink", "white"].map((mode) => {
                  const isActive = activeBlend === mode;

                  return (
                    <button
                      key={mode}
                      className={`btn ${isActive ? "btn-active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isReadOnly) return;
                        setLayerProps({ blend: mode });
                      }}
                    >
                      {isActive ? "✓ " : ""}
                      {mode[0].toUpperCase() + mode.slice(1)}
                    </button>
                  );
                })}
              </div>
              {/* Opacity slider – unchanged */}
              {!isReadOnly && (
                <div className="tt-opacity-row">
                  <span style={{ fontSize: 11, opacity: 0.8 }}>Opacity</span>
                  <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.05}
                    value={opacity}
                    onChange={handleOpacityChange}
                    disabled={isReadOnly}
                    style={{ flex: 1 }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      width: 34,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      opacity: 0.8,
                    }}
                  >
                    {Math.round(opacity * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Editor or empty message + Comments */}
          <div style={{ position: "relative" }}>
            <div style={{ position: "relative", zIndex: 1 }}>
              {hasImage ? (
                isReadOnly ? null : (
                  <DecalEditor
                    partKey={
                      partKey ||
                      (snap.viewMode === "fullbody"
                        ? "fullbody"
                        : Object.keys(snap.parts || {}).find(
                            (k) => snap.parts[k]
                          ) || "torso")
                    }
                  />
                )
              ) : (
                <div
                  className="tt-empty"
                  style={{
                    border: "1px dashed #333",
                    borderRadius: 8,
                    padding: 16,
                    color: "#cfcfcf",
                    textAlign: "center",
                  }}
                >
                  No image on this layer yet.
                  {!isReadOnly && (
                    <>
                      {" "}
                      Use <strong>Add Image…</strong>, or paint to add one.
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Panel ---------------- */

export default function LayersPanel({ partKey, layers: layersProp }) {
  const snap = useSnapshot(tattoo);
  const isReadOnly = !!tattoo.readOnly;

  // Billing paywall for the Save button (see handleSaveClick below).
  const { isEntitled, trialDaysLeft } = useSubscription();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const handleSaveClick = () => {
    if (isEntitled("save")) {
      saveProjectToFile();
    } else {
      setUpgradeOpen(true);
    }
  };

  // Figure out which body part this panel is for
  const resolvedPartKey =
    partKey ||
    getActivePartKey() ||
    (snap.viewMode === "fullbody"
      ? "fullbody"
      : Object.keys(snap.parts || {}).find((k) => snap.parts[k]) || "torso");

  const sex = snap.sex || "female";

  // Base list: prefer layers passed in, else global; then filter by sex + part
  const baseLayers = React.useMemo(() => {
    const raw =
      Array.isArray(layersProp) && layersProp.length
        ? layersProp
        : snap.layers || [];

    return raw.filter((L) => {
      if (!L) return false;

      // Sex-specific: if tagged, must match
      if (L.sex && L.sex !== sex) return false;

      // Part-specific
      const pk = resolvedPartKey;
      if (L.part) return L.part === pk;
      if (Array.isArray(L.partKeys)) return L.partKeys.includes(pk);

      // Untagged = global
      return true;
    });
  }, [layersProp, snap.layers, sex, resolvedPartKey]);

  // Final list: strip “guide only” placeholders in read-only mode
  const layers = React.useMemo(
    () =>
      isReadOnly
        ? baseLayers.filter((l) => !isLikelyPlaceholder(l))
        : baseLayers,
    [isReadOnly, baseLayers]
  );

  const expandedId = snap.editingLayerId || null;

  // Preload reaction counts whenever visible layer IDs change
  React.useEffect(() => {
    const ids = layers.map((l) => l.id).filter(Boolean);
    if (!ids.length) return;
    preloadReactionCounts(ids);
  }, [layers.map((l) => l.id).join("|")]);

  // Handle external "open layer" and "focus comments" events
  React.useEffect(() => {
    const openLayer = (layerId) => {
      if (!layerId) return;
      if (tattoo.editingLayerId !== layerId) {
        tattoo.editingLayerId = layerId;
        try {
          editLayer(layerId);
        } catch {}
      }
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `.layer-card[data-layer-id="${layerId}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    };

    const onOpen = (e) => openLayer(e?.detail?.layerId);
    const onFocusComments = (e) => openLayer(e?.detail?.layerId);

    window.addEventListener("tt:open-layer", onOpen);
    window.addEventListener("tt:focus-comments", onFocusComments);
    return () => {
      window.removeEventListener("tt:open-layer", onOpen);
      window.removeEventListener("tt:focus-comments", onFocusComments);
    };
  }, []);

  // Prefetch comments for visible layers
  React.useEffect(() => {
    layers.forEach((L) => {
      if (L?.id) fetchComments(L.id).catch(() => {});
    });
  }, [layers.map((l) => l.id).join("|")]);

  const onToggle = (id) => {
    tattoo.editingLayerId = expandedId === id ? null : id;
    if (tattoo.editingLayerId) {
      try {
        editLayer(tattoo.editingLayerId);
      } catch {}
    }
  };

  return (
    <div className="layers-panel">
      <div className="label">{isReadOnly ? " " : "Layers"}</div>

      {!isReadOnly ? (
        <div
          className="row"
          style={{ gap: 8, marginBottom: 10, flexWrap: "wrap" }}
        >
          <button className="btn" onClick={handleSaveClick}>
            Save
          </button>
          <label className="btn" style={{ cursor: "pointer" }}>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                loadProjectFromFile(file);
              }}
              style={{ display: "none" }}
            />
            Open
          </label>
          <button
            className="btn"
            onClick={() => exportUVSheetPNG({ includeGuide: false })}
          >
            Export
          </button>

          <button
            className="btn"
            onClick={() =>
              exportUVSheetPNG({ includeGuide: true, guideAlpha: 0.6 })
            }
          >
            Export With Guide
          </button>

          <button
            className="btn"
            title="Each design upright and cropped, 2048px (zip if more than one)"
            onClick={() => exportDesignsPNG()}
          >
            Export Designs
          </button>
        </div>
      ) : (
        <div className="tt-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Viewing only. Use 👁 to show/hide layers.
        </div>
      )}

      {layers.length === 0 ? (
        <div className="tt-muted">No layers yet.</div>
      ) : (
        <div className="layers-list">
          {layers.map((L) => (
            <Row
              key={L.id}
              layer={L}
              partKey={resolvedPartKey}
              expanded={expandedId === L.id}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        trialDaysLeft={trialDaysLeft}
        reason="save"
      />
    </div>
  );
}
